import React, { useEffect, useState } from 'react';
import { FileText, Download, FileSpreadsheet, File as FileIcon } from 'lucide-react';

// Renders a file the agent saved to ~/shared and mentioned by path. The URL points at
// the Cloudflare worker /f/<token>/<name> route (link-shareable), which proxies to the
// sandbox's :7892 file server. Display/download is direct — no preview service.
// (doc 148; mirrors how the messaging-gateway "Deliverable Mode" delivers files.)

const IMG = /\.(png|jpe?g|gif|webp|svg|bmp|ico)$/i;
const PDF = /\.pdf$/i;
const CSV = /\.csv$/i;
const AUDIO = /\.(mp3|wav|ogg|m4a)$/i;
const VIDEO = /\.(mp4|mov|webm|m4v)$/i;
const TEXT = /\.(txt|md|json|log|tsv|yaml|yml)$/i;

function Shell({ name, url, icon, children }) {
  return (
    <div className="mt-1.5 border border-gray-700/60 rounded-md overflow-hidden bg-black/20">
      <div className="flex items-center justify-between px-2.5 py-1.5 border-b border-gray-700/40">
        <span className="flex items-center gap-1.5 text-[12px] text-gray-300 truncate">
          {icon}<span className="truncate">{name}</span>
        </span>
        <a href={url} download={name} target="_blank" rel="noopener noreferrer"
           className="shrink-0 text-gray-400 hover:text-gray-200" title="Download">
          <Download size={13} />
        </a>
      </div>
      {children}
    </div>
  );
}

function CsvTable({ url }) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let alive = true;
    fetch(url).then((r) => r.text()).then((t) => {
      if (!alive) return;
      const lines = t.split(/\r?\n/).filter((l) => l.length).slice(0, 31);
      setRows(lines.map((l) => l.split(',')));
    }).catch(() => alive && setErr(true));
    return () => { alive = false; };
  }, [url]);
  if (err) return <div className="px-2.5 py-2 text-[11px] text-gray-500">CSV preview unavailable — use Download.</div>;
  if (!rows) return <div className="px-2.5 py-2 text-[11px] text-gray-500">Loading…</div>;
  const [head, ...body] = rows;
  return (
    <div className="overflow-auto max-h-[260px]">
      <table className="text-[11px] w-full border-collapse">
        <thead>
          <tr>{head.map((h, i) => (
            <th key={i} className="text-left px-2 py-1 border-b border-gray-700/50 text-gray-400 font-medium sticky top-0 bg-[#1a1c2a]">{h}</th>
          ))}</tr>
        </thead>
        <tbody>
          {body.map((r, i) => (
            <tr key={i} className="odd:bg-white/[0.02]">
              {r.map((c, j) => <td key={j} className="px-2 py-0.5 text-gray-300 whitespace-nowrap">{c}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TextPreview({ url }) {
  const [txt, setTxt] = useState(null);
  useEffect(() => {
    let alive = true;
    fetch(url).then((r) => r.text()).then((t) => alive && setTxt(t.slice(0, 4000)))
      .catch(() => alive && setTxt(null));
    return () => { alive = false; };
  }, [url]);
  if (txt == null) return <div className="px-2.5 py-2 text-[11px] text-gray-500">Loading…</div>;
  return <pre className="px-2.5 py-2 text-[11px] text-gray-300 whitespace-pre-wrap max-h-[260px] overflow-auto">{txt}</pre>;
}

export default function SharedFile({ url, name }) {
  if (IMG.test(name)) {
    return (
      <a href={url} target="_blank" rel="noopener noreferrer" className="block mt-1.5">
        <img src={url} alt={name} className="max-w-full rounded-md border border-gray-700/60" loading="lazy" />
      </a>
    );
  }
  if (PDF.test(name)) {
    return (
      <Shell name={name} url={url} icon={<FileText size={13} className="text-red-400" />}>
        <iframe src={url} title={name} className="w-full h-[520px] bg-white" />
      </Shell>
    );
  }
  if (CSV.test(name)) {
    return (
      <Shell name={name} url={url} icon={<FileSpreadsheet size={13} className="text-green-400" />}>
        <CsvTable url={url} />
      </Shell>
    );
  }
  if (AUDIO.test(name)) {
    return (
      <Shell name={name} url={url} icon={<FileIcon size={13} className="text-blue-400" />}>
        <audio controls src={url} className="w-full" />
      </Shell>
    );
  }
  if (VIDEO.test(name)) {
    return (
      <Shell name={name} url={url} icon={<FileIcon size={13} className="text-blue-400" />}>
        <video controls src={url} className="w-full max-h-[420px]" />
      </Shell>
    );
  }
  if (TEXT.test(name)) {
    return (
      <Shell name={name} url={url} icon={<FileText size={13} className="text-gray-400" />}>
        <TextPreview url={url} />
      </Shell>
    );
  }
  // Unknown/binary → download chip only.
  return (
    <a href={url} download={name} target="_blank" rel="noopener noreferrer"
       className="mt-1.5 inline-flex items-center gap-2 border border-gray-700/60 rounded-md px-3 py-1.5 text-[12px] text-gray-300 hover:bg-white/[0.04]">
      <FileIcon size={13} className="text-gray-400" />
      <span className="truncate max-w-[260px]">{name}</span>
      <Download size={13} className="text-gray-500" />
    </a>
  );
}
